/**
 * BoosterV2 Plan View - Main plan dashboard after generation
 * Tabs: Podsumowanie | Treści | Miejsca | Ustawienia
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { 
  ArrowLeft, Copy, Check, ExternalLink, Calendar as CalendarIcon, 
  MessageSquare, Clock, CheckCircle, Loader2, Play, Pause,
  MoreHorizontal, Edit, Sparkles, X, Eye, FileText, Download, Link, Unlink,
  ImagePlus, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose 
} from '@/components/ui/sheet';
import { format, setHours, setMinutes, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import GuidancePanel from './components/GuidancePanel';

import { API_BASE as API } from '@/utils/api';

// Tabs
const TABS = [
  { id: 'summary', label: 'Podsumowanie' },
  { id: 'content', label: 'Treści' },
  { id: 'places', label: 'Miejsca' },
  { id: 'settings', label: 'Ustawienia' }
];

// Post status config with semantic colors
const POST_STATUS = {
  draft: { 
    label: 'Szkic', 
    color: 'bg-gray-100 text-gray-600 border-gray-200',
    dot: 'bg-gray-400'
  },
  approved: { 
    label: 'Zatwierdzony', 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    dot: 'bg-orange-500'
  },
  scheduled: { 
    label: 'Zaplanowany', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    dot: 'bg-blue-500'
  },
  published: { 
    label: 'Opublikowany', 
    color: 'bg-green-100 text-green-700 border-green-200',
    dot: 'bg-green-500'
  },
  skipped: { 
    label: 'Pominięty', 
    color: 'bg-amber-50 text-amber-600 border-amber-200',
    dot: 'bg-amber-400'
  }
};

// Status actions for dropdown
const STATUS_ACTIONS = [
  { status: 'draft', label: 'Cofnij do szkicu', icon: '📝' },
  { status: 'approved', label: 'Zatwierdź do publikacji', icon: '✓' },
  { status: 'scheduled', label: 'Zaplanuj publikację', icon: '📅' },
  { status: 'published', label: 'Oznacz jako opublikowany', icon: '✅' },
  { status: 'skipped', label: 'Pomiń ten post', icon: '⏭️' }
];

// Post Preview Drawer Component
function PostPreviewDrawer({ post, open, onOpenChange, onCopy, onEdit }) {
  const [copied, setCopied] = useState(false);
  
  if (!post) return null;
  
  const status = POST_STATUS[post.status] || POST_STATUS.draft;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(post.text);
    setCopied(true);
    onCopy?.(post);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Skopiowano do schowka!');
  };
  
  const handleEdit = () => {
    onOpenChange(false);
    onEdit?.(post);
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              post.status === 'published' ? 'bg-green-100' :
              post.status === 'approved' ? 'bg-orange-100' :
              post.status === 'scheduled' ? 'bg-blue-100' :
              'bg-gray-100'
            }`}>
              <FileText className={`w-6 h-6 ${
                post.status === 'published' ? 'text-green-600' :
                post.status === 'approved' ? 'text-orange-600' :
                post.status === 'scheduled' ? 'text-blue-600' :
                'text-gray-600'
              }`} />
            </div>
            <div>
              <SheetTitle className="text-xl">Dzień {post.day_index}</SheetTitle>
              <SheetDescription className="flex items-center gap-2 mt-1">
                {post.publish_at ? (
                  <span className="text-blue-600 font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {format(parseISO(post.publish_at), 'd MMMM yyyy, HH:mm', { locale: pl })}
                  </span>
                ) : (
                  <span className="text-gray-500">Do zaplanowania</span>
                )}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>
        
        {/* Status Badge */}
        <div className="py-4 border-b">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1.5 ${status.color}`}>
              <span className={`w-2 h-2 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>
        </div>
        
        {/* Content Preview */}
        <div className="py-6">
          <h4 className="text-sm font-medium text-gray-500 mb-3">Treść posta</h4>
          <div className={`bg-gray-50 rounded-xl p-4 border ${
            post.status === 'skipped' ? 'opacity-60' : ''
          }`}>
            <p className={`whitespace-pre-wrap leading-relaxed text-gray-700 ${
              post.status === 'skipped' ? 'line-through' : ''
            }`}>
              {post.text}
            </p>
          </div>
          
          {/* Character count */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <span className="text-gray-400">{post.text?.length || 0} znaków</span>
            {post.text?.length > 280 && (
              <span className="text-amber-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Przekracza limit Twittera (280)
              </span>
            )}
          </div>
        </div>
        
        {/* Meta info */}
        {(post.created_at || post.updated_at) && (
          <div className="py-4 border-t border-gray-100 text-xs text-gray-400 space-y-1">
            {post.created_at && (
              <div>Utworzono: {format(parseISO(post.created_at), 'd MMM yyyy, HH:mm', { locale: pl })}</div>
            )}
            {post.updated_at && post.updated_at !== post.created_at && (
              <div>Ostatnia edycja: {format(parseISO(post.updated_at), 'd MMM yyyy, HH:mm', { locale: pl })}</div>
            )}
          </div>
        )}
        
        {/* Actions */}
        <SheetFooter className="pt-4 border-t gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleCopy}
            className="flex-1 gap-2"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                Skopiowano
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Kopiuj
              </>
            )}
          </Button>
          <Button
            onClick={handleEdit}
            className="flex-1 gap-2 bg-orange-600 hover:bg-orange-700"
          >
            <Edit className="w-4 h-4" />
            Edytuj
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// Post card component with status workflow and inline edit
function PostCard({ post, onStatusChange, onCopy, onEdit, onSave, onSchedule, onPreview, onPublishFB, fbConnected, onMediaUpdate, groups }) {
  const [copied, setCopied] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.text);
  const [saving, setSaving] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(post.publish_at ? parseISO(post.publish_at) : null);
  const [scheduleTime, setScheduleTime] = useState(post.publish_at ? format(parseISO(post.publish_at), 'HH:mm') : '18:00');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [showGroupPublish, setShowGroupPublish] = useState(false);
  const [postedGroups, setPostedGroups] = useState({});
  const [publishingFB, setPublishingFB] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { token } = useAuth();
  const status = POST_STATUS[post.status] || POST_STATUS.draft;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(post.text);
    setCopied(true);
    onCopy?.(post);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Skopiowano do schowka!');
  };
  
  const handleStatusChange = async (newStatus) => {
    if (newStatus === post.status) {
      setShowStatusMenu(false);
      return;
    }
    
    setUpdating(true);
    setShowStatusMenu(false);
    
    try {
      await onStatusChange?.(post, newStatus);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleStartEdit = () => {
    setEditText(post.text);
    setIsEditing(true);
  };
  
  const handleCancelEdit = () => {
    setEditText(post.text);
    setIsEditing(false);
  };
  
  const handleSaveEdit = async () => {
    if (editText.trim() === post.text) {
      setIsEditing(false);
      return;
    }
    
    setSaving(true);
    try {
      await onSave?.(post, editText.trim());
      setIsEditing(false);
    } catch (err) {
      // Keep editing mode on error
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveSchedule = async () => {
    if (!scheduleDate) {
      toast.error('Wybierz datę publikacji');
      return;
    }
    
    // Combine date and time
    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const publishAt = setMinutes(setHours(scheduleDate, hours), minutes);
    
    setScheduleSaving(true);
    try {
      await onSchedule?.(post, publishAt.toISOString());
      setShowSchedule(false);
      toast.success('Zaplanowano publikację');
    } catch (err) {
      toast.error('Nie udało się zaplanować');
    } finally {
      setScheduleSaving(false);
    }
  };
  
  const handleClearSchedule = async () => {
    setScheduleSaving(true);
    try {
      await onSchedule?.(post, null);
      setScheduleDate(null);
      setShowSchedule(false);
      toast.success('Usunięto harmonogram');
    } catch (err) {
      toast.error('Błąd usuwania harmonogramu');
    } finally {
      setScheduleSaving(false);
    }
  };
  
  // Image upload handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik graficzny (JPG, PNG, WEBP)');
      return;
    }
    
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Plik zbyt duży (max 12MB)');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadRes = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (!uploadRes.ok) throw new Error('Upload failed');
      
      const uploadData = await uploadRes.json();
      const imageUrl = uploadData.urls?.medium || uploadData.urls?.original;
      
      // Update post media
      await onMediaUpdate?.(post, [imageUrl]);
      toast.success('Grafika dodana!');
    } catch (err) {
      toast.error('Nie udało się wgrać grafiki');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  const handleRemoveImage = async () => {
    try {
      await onMediaUpdate?.(post, []);
      toast.success('Grafika usunięta');
    } catch (err) {
      toast.error('Nie udało się usunąć grafiki');
    }
  };
  
  // P2.3: Publish to Facebook
  const handlePublishToFB = async () => {
    if (!fbConnected) {
      toast.error('Połącz konto Facebook w zakładce Ustawienia');
      return;
    }
    
    setPublishingFB(true);
    try {
      await onPublishFB?.(post);
    } finally {
      setPublishingFB(false);
    }
  };
  
  // Update local text when post changes
  React.useEffect(() => {
    if (!isEditing) {
      setEditText(post.text);
    }
  }, [post.text, isEditing]);
  
  // Update schedule when post changes
  React.useEffect(() => {
    if (post.publish_at) {
      try {
        const dt = parseISO(post.publish_at);
        setScheduleDate(dt);
        setScheduleTime(format(dt, 'HH:mm'));
      } catch (e) {}
    }
  }, [post.publish_at]);
  
  return (
    <div className={`bg-white rounded-xl border-2 p-5 transition-all ${
      post.status === 'published' ? 'border-green-200 bg-green-50/30' :
      post.status === 'approved' ? 'border-orange-200' :
      post.status === 'scheduled' ? 'border-blue-200 bg-blue-50/30' :
      post.status === 'skipped' ? 'border-amber-100 bg-amber-50/20 opacity-70' :
      'border-gray-200'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            post.status === 'published' ? 'bg-green-100' :
            post.status === 'approved' ? 'bg-orange-100' :
            post.status === 'scheduled' ? 'bg-blue-100' :
            'bg-gray-100'
          }`}>
            <CalendarIcon className={`w-5 h-5 ${
              post.status === 'published' ? 'text-green-600' :
              post.status === 'approved' ? 'text-orange-600' :
              post.status === 'scheduled' ? 'text-blue-600' :
              'text-gray-600'
            }`} />
          </div>
          <div>
            <div className="font-semibold text-gray-900">Dzień {post.day_index}</div>
            {/* Schedule display/picker */}
            <Popover open={showSchedule} onOpenChange={setShowSchedule}>
              <PopoverTrigger asChild>
                <button 
                  className={`text-sm flex items-center gap-1 hover:text-orange-600 transition-colors ${
                    post.publish_at ? 'text-blue-600 font-medium' : 'text-gray-500'
                  }`}
                  disabled={isEditing}
                >
                  <Clock className="w-3 h-3" />
                  {post.publish_at 
                    ? format(parseISO(post.publish_at), 'd MMM, HH:mm', { locale: pl })
                    : 'Zaplanuj publikację'
                  }
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="p-4 space-y-4">
                  <div className="font-medium text-sm text-gray-700">Wybierz datę i godzinę</div>
                  <Calendar
                    mode="single"
                    selected={scheduleDate}
                    onSelect={setScheduleDate}
                    locale={pl}
                    disabled={(date) => date < new Date()}
                    className="rounded-md border"
                  />
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Godzina:</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveSchedule}
                      disabled={scheduleSaving || !scheduleDate}
                      className="flex-1 gap-1 bg-orange-600 hover:bg-orange-700"
                    >
                      {scheduleSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Zapisz
                    </Button>
                    {post.publish_at && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleClearSchedule}
                        disabled={scheduleSaving}
                        className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <X className="w-3 h-3" />
                        Usuń
                      </Button>
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        
        {/* Status badge with dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowStatusMenu(!showStatusMenu)}
            disabled={updating || isEditing}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1.5 transition-all hover:shadow-sm ${status.color} ${isEditing ? 'opacity-50' : ''}`}
          >
            {updating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <span className={`w-2 h-2 rounded-full ${status.dot}`} />
            )}
            {status.label}
            <MoreHorizontal className="w-3 h-3 ml-1" />
          </button>
          
          {/* Status dropdown menu */}
          {showStatusMenu && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowStatusMenu(false)} 
              />
              <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px]">
                {STATUS_ACTIONS.map(action => (
                  <button
                    key={action.status}
                    onClick={() => handleStatusChange(action.status)}
                    disabled={action.status === post.status}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                      action.status === post.status 
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed' 
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span>{action.icon}</span>
                    <span>{action.label}</span>
                    {action.status === post.status && (
                      <Check className="w-4 h-4 ml-auto text-green-500" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="mb-4">
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full min-h-[120px] p-3 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-y text-gray-700 leading-relaxed"
              placeholder="Treść posta..."
              autoFocus
              data-testid="inline-edit-textarea"
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={saving || editText.trim() === ''}
                className="gap-2 bg-orange-600 hover:bg-orange-700"
                data-testid="save-edit-button"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Zapisz
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                disabled={saving}
                data-testid="cancel-edit-button"
              >
                Anuluj
              </Button>
              <span className="text-xs text-gray-400 ml-auto">
                {editText.length} znaków
              </span>
            </div>
          </div>
        ) : (
          <p className={`whitespace-pre-wrap leading-relaxed ${
            post.status === 'skipped' ? 'text-gray-400 line-through' : 'text-gray-700'
          }`}>
            {post.text}
          </p>
        )}
      </div>
      
      {/* Image section */}
      <div className="mb-4">
        {post.media && post.media.length > 0 ? (
          <div className="relative group rounded-lg overflow-hidden border border-gray-200">
            <img 
              src={`${API}${post.media[0]}`} 
              alt="Grafika posta" 
              className="w-full h-48 object-cover"
              data-testid={`post-image-${post._id || post.id}`}
            />
            {!isEditing && (
              <button
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                title="Usuń grafikę"
                data-testid={`remove-image-${post._id || post.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : !isEditing && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              data-testid={`image-input-${post._id || post.id}`}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-gray-200 rounded-lg py-3 px-4 flex items-center justify-center gap-2 text-sm text-gray-400 hover:border-orange-300 hover:text-orange-500 hover:bg-orange-50/50 transition-all"
              data-testid={`add-image-${post._id || post.id}`}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Wgrywanie...
                </>
              ) : (
                <>
                  <ImagePlus className="w-4 h-4" />
                  Dodaj grafikę
                </>
              )}
            </button>
          </div>
        )}
      </div>
      
      {/* Actions - hide when editing */}
      {!isEditing && (
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-2"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                Skopiowano
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Kopiuj
              </>
            )}
          </Button>
          
          {/* Quick action: Approve or Publish */}
          {post.status === 'draft' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange('approved')}
              disabled={updating}
              className="gap-2 border-orange-200 text-orange-700 hover:bg-orange-50"
            >
              <CheckCircle className="w-4 h-4" />
              Zatwierdź
            </Button>
          )}
          
          {post.status === 'approved' && (
            <Button
              size="sm"
              onClick={() => handleStatusChange('published')}
              disabled={updating}
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <Play className="w-4 h-4" />
              Opublikuj
            </Button>
          )}
          
          {/* P2.3: Facebook Publish button */}
          {(post.status === 'approved' || post.status === 'scheduled' || post.status === 'published') && fbConnected && (
            <Button
              size="sm"
              onClick={handlePublishToFB}
              disabled={publishingFB || updating}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="publish-fb-button"
            >
              {publishingFB ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              )}
              Opublikuj na FB
            </Button>
          )}
          
          {/* Group publish helper button */}
          {(post.status === 'approved' || post.status === 'scheduled' || post.status === 'published') && groups && groups.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowGroupPublish(!showGroupPublish)}
              className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              data-testid="publish-groups-button"
            >
              <Copy className="w-4 h-4" />
              Grupy ({groups.filter(g => g.isActive !== false).length})
            </Button>
          )}
          
          <div className="flex items-center gap-1 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPreview?.(post)}
              title="Podgląd"
              data-testid="preview-post-button"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleStartEdit}
              title="Edytuj treść"
              data-testid="edit-post-button"
            >
              <Edit className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      
      {/* Group publish helper panel */}
      {showGroupPublish && groups && groups.length > 0 && (
        <div className="mt-3 p-4 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="group-publish-panel">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-indigo-800">Publikuj do grup</span>
            <Button variant="ghost" size="sm" onClick={() => setShowGroupPublish(false)} className="h-6 w-6 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="mb-3">
            <Button
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(post.text);
                toast.success('Tekst skopiowany do schowka!');
              }}
              className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white w-full"
              data-testid="copy-post-text-button"
            >
              <Copy className="w-4 h-4" />
              Kopiuj tekst posta
            </Button>
          </div>
          
          <div className="space-y-2">
            {groups.filter(g => g.isActive !== false).map((group, idx) => (
              <div key={group.id || idx} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-indigo-100">
                <input
                  type="checkbox"
                  checked={!!postedGroups[group.id || idx]}
                  onChange={() => setPostedGroups(prev => ({ ...prev, [group.id || idx]: !prev[group.id || idx] }))}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300"
                  data-testid={`group-checkbox-${idx}`}
                />
                <span className={`flex-1 text-sm ${postedGroups[group.id || idx] ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                  {group.name}
                </span>
                {group.url ? (
                  <a
                    href={group.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    data-testid={`open-group-link-${idx}`}
                  >
                    Otwórz <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <a
                    href={`https://www.facebook.com/groups/${group.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    data-testid={`open-group-link-${idx}`}
                  >
                    Otwórz <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
          
          {Object.values(postedGroups).some(Boolean) && (
            <p className="text-xs text-indigo-600 mt-2">
              Zaznaczono {Object.values(postedGroups).filter(Boolean).length}/{groups.filter(g => g.isActive !== false).length} grup
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Summary tab with Statistics Dashboard (P2.2) + CSV Export (P2.2b)
function SummaryTab({ plan, posts }) {
  // Calculate all stats per P2.2 spec
  const stats = {
    total: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
    approved: posts.filter(p => p.status === 'approved').length,
    draft: posts.filter(p => p.status === 'draft').length,
    skipped: posts.filter(p => p.status === 'skipped').length
  };
  
  // Calculate derived stats
  stats.toPublish = stats.draft + stats.approved + stats.scheduled;
  stats.completionRate = stats.total > 0 
    ? Math.round((stats.published / stats.total) * 100) 
    : 0;
  
  // Find today's scheduled posts
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const todayScheduled = posts.filter(p => {
    if (p.status !== 'scheduled' || !p.publish_at) return false;
    try {
      const publishDate = parseISO(p.publish_at);
      return publishDate >= today && publishDate < tomorrow;
    } catch { return false; }
  });
  
  // Find next scheduled post
  const nextScheduled = posts
    .filter(p => p.status === 'scheduled' && p.publish_at)
    .sort((a, b) => {
      try {
        return parseISO(a.publish_at) - parseISO(b.publish_at);
      } catch { return 0; }
    })[0];
  
  // Find today's task (first unpublished/unskipped)
  const todayTask = posts.find(p => p.status !== 'published' && p.status !== 'skipped');
  
  // P2.2b: CSV Export function
  const handleExportCSV = () => {
    const now = new Date();
    const timestamp = format(now, 'yyyy-MM-dd_HH-mm');
    
    // Create slug from business name
    const businessSlug = (plan?.business_name || 'kampania')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .substring(0, 30);
    
    // CSV Headers
    const headers = [
      'exported_at',
      'campaign_id',
      'campaign_name',
      'business_name',
      'total_posts',
      'published_count',
      'scheduled_count',
      'to_publish_count',
      'completion_rate_percent',
      'draft_count',
      'approved_count',
      'skipped_count',
      'today_scheduled_count',
      'next_scheduled_at'
    ];
    
    // CSV Values
    const values = [
      format(now, 'yyyy-MM-dd HH:mm:ss'),
      plan?.id || '',
      plan?.goal_type || '',
      plan?.business_name || '',
      stats.total,
      stats.published,
      stats.scheduled,
      stats.toPublish,
      stats.completionRate,
      stats.draft,
      stats.approved,
      stats.skipped,
      todayScheduled.length,
      nextScheduled?.publish_at || ''
    ];
    
    // Build CSV with semicolon separator (better for Polish Excel)
    const csvContent = [
      headers.join(';'),
      values.map(v => `"${v}"`).join(';')
    ].join('\n');
    
    // Add UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Download file
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `booster_stats_${businessSlug}_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    
    toast.success('Wyeksportowano statystyki do CSV');
  };
  
  return (
    <div className="space-y-6" data-testid="stats-dashboard">
      {/* Header with Export Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Statystyki kampanii</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          className="gap-2"
          data-testid="export-csv-button"
        >
          <Download className="w-4 h-4" />
          Eksportuj statystyki (CSV)
        </Button>
      </div>
      
      {/* Main Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stats-cards-grid">
        {/* Total */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-card-total">
          <div className="flex items-center justify-between mb-2">
            <MessageSquare className="w-5 h-5 text-gray-400" />
          </div>
          <div className="text-3xl font-bold text-gray-900" data-testid="stat-value-total">{stats.total}</div>
          <div className="text-sm text-gray-500">Wszystkich</div>
        </div>
        
        {/* Published */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-card-published">
          <div className="flex items-center justify-between mb-2">
            <Check className="w-5 h-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600" data-testid="stat-value-published">{stats.published}</div>
          <div className="text-sm text-green-700">Opublikowanych</div>
        </div>
        
        {/* Scheduled */}
        <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl border border-blue-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-card-scheduled">
          <div className="flex items-center justify-between mb-2">
            <CalendarIcon className="w-5 h-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-600" data-testid="stat-value-scheduled">{stats.scheduled}</div>
          <div className="text-sm text-blue-700">Zaplanowanych</div>
        </div>
        
        {/* To Publish */}
        <div className="bg-gradient-to-br from-orange-50 to-purple-50 rounded-xl border border-orange-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-card-to-publish">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-5 h-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-orange-600" data-testid="stat-value-to-publish">{stats.toPublish}</div>
          <div className="text-sm text-orange-700">Do opublikowania</div>
        </div>
        
        {/* Completion Rate */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5 hover:shadow-md transition-shadow" data-testid="stat-card-completion">
          <div className="flex items-center justify-between mb-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
          </div>
          <div className="text-3xl font-bold text-amber-600" data-testid="stat-value-completion">{stats.completionRate}%</div>
          <div className="text-sm text-amber-700">Ukończenie planu</div>
        </div>
      </div>
      
      {/* Status Breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="status-breakdown">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-400" />
          Rozkład statusów
        </h3>
        <div className="space-y-3">
          {/* Progress bar */}
          <div className="h-4 rounded-full bg-gray-100 overflow-hidden flex" data-testid="status-progress-bar">
            {stats.published > 0 && (
              <div 
                className="bg-green-500 h-full transition-all" 
                style={{ width: `${(stats.published / stats.total) * 100}%` }}
                title={`Opublikowane: ${stats.published}`}
                data-testid="progress-published"
              />
            )}
            {stats.scheduled > 0 && (
              <div 
                className="bg-blue-500 h-full transition-all" 
                style={{ width: `${(stats.scheduled / stats.total) * 100}%` }}
                title={`Zaplanowane: ${stats.scheduled}`}
                data-testid="progress-scheduled"
              />
            )}
            {stats.approved > 0 && (
              <div 
                className="bg-orange-500 h-full transition-all" 
                style={{ width: `${(stats.approved / stats.total) * 100}%` }}
                title={`Zatwierdzone: ${stats.approved}`}
                data-testid="progress-approved"
              />
            )}
            {stats.draft > 0 && (
              <div 
                className="bg-gray-400 h-full transition-all" 
                style={{ width: `${(stats.draft / stats.total) * 100}%` }}
                title={`Szkice: ${stats.draft}`}
                data-testid="progress-draft"
              />
            )}
            {stats.skipped > 0 && (
              <div 
                className="bg-amber-400 h-full transition-all" 
                style={{ width: `${(stats.skipped / stats.total) * 100}%` }}
                title={`Pominięte: ${stats.skipped}`}
                data-testid="progress-skipped"
              />
            )}
          </div>
          
          {/* Legend */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm" data-testid="status-legend">
            <div className="flex items-center gap-2" data-testid="legend-published">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-gray-600">Opublikowane ({stats.published})</span>
            </div>
            <div className="flex items-center gap-2" data-testid="legend-scheduled">
              <span className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-600">Zaplanowane ({stats.scheduled})</span>
            </div>
            <div className="flex items-center gap-2" data-testid="legend-approved">
              <span className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-gray-600">Zatwierdzone ({stats.approved})</span>
            </div>
            <div className="flex items-center gap-2" data-testid="legend-draft">
              <span className="w-3 h-3 rounded-full bg-gray-400" />
              <span className="text-gray-600">Szkice ({stats.draft})</span>
            </div>
            <div className="flex items-center gap-2" data-testid="legend-skipped">
              <span className="w-3 h-3 rounded-full bg-amber-400" />
              <span className="text-gray-600">Pominięte ({stats.skipped})</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Schedule Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Today's Scheduled */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="today-scheduled-section">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-blue-500" />
            Dzisiejsze zaplanowane
          </h3>
          {todayScheduled.length > 0 ? (
            <div className="space-y-3" data-testid="today-scheduled-list">
              {todayScheduled.map((post, i) => (
                <div key={post.id || i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <div className="font-medium text-gray-900">Dzień {post.day_index}</div>
                    <div className="text-sm text-blue-600">
                      {post.publish_at && format(parseISO(post.publish_at), 'HH:mm', { locale: pl })}
                    </div>
                  </div>
                  <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                    Zaplanowany
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-sm py-4 text-center" data-testid="today-scheduled-empty">
              Brak postów zaplanowanych na dziś
            </div>
          )}
        </div>
        
        {/* Next Scheduled */}
        <div className="bg-white rounded-xl border border-gray-200 p-6" data-testid="next-scheduled-section">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Najbliższy termin publikacji
          </h3>
          {nextScheduled ? (
            <div className="space-y-3" data-testid="next-scheduled-content">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <div className="font-medium text-gray-900">Dzień {nextScheduled.day_index}</div>
                  <div className="text-sm text-orange-600 font-medium" data-testid="next-scheduled-date">
                    {nextScheduled.publish_at && format(parseISO(nextScheduled.publish_at), 'd MMMM yyyy, HH:mm', { locale: pl })}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{nextScheduled.text}</p>
            </div>
          ) : (
            <div className="text-gray-500 text-sm py-4 text-center" data-testid="next-scheduled-empty">
              Brak zaplanowanych publikacji
            </div>
          )}
        </div>
      </div>
      
      {/* Today's Task */}
      {todayTask && (
        <div className="bg-gradient-to-r from-orange-50 to-purple-50 rounded-xl border border-orange-200 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-orange-800">Następne zadanie</h3>
          </div>
          <p className="text-gray-700 mb-4 line-clamp-3">{todayTask.text}</p>
          <div className="flex gap-2">
            <Button 
              size="sm" 
              className="bg-orange-600 hover:bg-orange-700 gap-2"
              onClick={() => {
                navigator.clipboard.writeText(todayTask.text);
                toast.success('Skopiowano!');
              }}
            >
              <Copy className="w-4 h-4" />
              Kopiuj treść
            </Button>
          </div>
        </div>
      )}
      
      {/* Plan info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Informacje o planie</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Cel</dt>
            <dd className="text-gray-900 font-medium">{plan.goal_type || '-'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Firma</dt>
            <dd className="text-gray-900 font-medium">{plan.business_name || '-'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Długość</dt>
            <dd className="text-gray-900 font-medium">{plan.duration || 7} dni</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// Content tab
function ContentTab({ posts, onStatusChange, onBulkStatusChange, onCopy, onEdit, onSave, onSchedule, onPreview, onPublishFB, fbConnected, onMediaUpdate, groups }) {
  // Filter state
  const [filter, setFilter] = useState('all');
  const [bulkLoading, setBulkLoading] = useState(false);
  
  const filteredPosts = filter === 'all' 
    ? posts 
    : posts.filter(p => p.status === filter);
  
  const counts = {
    all: posts.length,
    draft: posts.filter(p => p.status === 'draft').length,
    approved: posts.filter(p => p.status === 'approved').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
    published: posts.filter(p => p.status === 'published').length,
    skipped: posts.filter(p => p.status === 'skipped').length
  };
  
  const handleBulkAction = async (fromStatus, newStatus) => {
    setBulkLoading(true);
    try {
      await onBulkStatusChange?.(fromStatus, newStatus);
    } finally {
      setBulkLoading(false);
    }
  };
  
  // P2.2c: CSV Export posts list
  const handleExportPostsCSV = () => {
    const STATUS_LABELS = {
      draft: 'Szkic', approved: 'Zatwierdzony', scheduled: 'Zaplanowany',
      published: 'Opublikowany', skipped: 'Pominięty'
    };
    
    const headers = ['Dzień', 'Status', 'Treść', 'Data publikacji', 'Ma grafikę'];
    const rows = posts.map(p => [
      p.day_index,
      STATUS_LABELS[p.status] || p.status,
      `"${(p.text || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      p.publish_at ? new Date(p.publish_at).toLocaleString('pl-PL') : '-',
      p.media && p.media.length > 0 ? 'Tak' : 'Nie'
    ]);
    
    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `posty_kampania_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Wyeksportowano listę postów');
  };
  
  // Show bulk actions when there are actionable posts
  const showBulkActions = counts.draft > 0 || counts.approved > 0;
  
  return (
    <div className="space-y-4">
      {/* Bulk actions bar */}
      {showBulkActions && (
        <div className="bg-gradient-to-r from-orange-50 to-purple-50 rounded-xl border border-orange-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">Szybkie akcje</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {counts.draft > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('draft', 'approved')}
                  disabled={bulkLoading}
                  className="gap-2 border-orange-200 text-orange-700 hover:bg-orange-100"
                  data-testid="bulk-approve-drafts-button"
                >
                  {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                  Zatwierdź wszystkie szkice ({counts.draft})
                </Button>
              )}
              {counts.approved > 0 && (
                <Button
                  size="sm"
                  onClick={() => handleBulkAction('approved', 'published')}
                  disabled={bulkLoading}
                  className="gap-2 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="bulk-publish-approved-button"
                >
                  {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  Opublikuj zatwierdzone ({counts.approved})
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Filter tabs + CSV export */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { id: 'all', label: 'Wszystkie' },
            { id: 'draft', label: 'Szkice' },
            { id: 'approved', label: 'Zatwierdzone' },
            { id: 'scheduled', label: 'Zaplanowane' },
            { id: 'published', label: 'Opublikowane' },
            { id: 'skipped', label: 'Pominięte' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === tab.id 
                  ? 'bg-orange-100 text-orange-700' 
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {tab.label} ({counts[tab.id]})
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportPostsCSV}
          className="gap-2 text-gray-600"
          data-testid="export-posts-csv"
        >
          <Download className="w-4 h-4" />
          Eksportuj posty (CSV)
        </Button>
      </div>
      
      {/* Posts list */}
      {filteredPosts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Brak postów w tej kategorii
        </div>
      ) : (
        filteredPosts.map((post) => (
          <PostCard 
            key={post.id} 
            post={post}
            onStatusChange={onStatusChange}
            onCopy={onCopy}
            onEdit={onEdit}
            onSave={onSave}
            onSchedule={onSchedule}
            onPreview={onPreview}
            onPublishFB={onPublishFB}
            fbConnected={fbConnected}
            onMediaUpdate={onMediaUpdate}
            groups={groups}
          />
        ))
      )}
    </div>
  );
}

// Places tab
function PlacesTab({ plan }) {
  const places = plan.places || { fanpage: {}, groups: [] };
  
  return (
    <div className="space-y-6">
      {/* Fanpage */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Fanpage</h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">{places.fanpage?.name || 'Brak'}</div>
            {places.fanpage?.url && (
              <a 
                href={places.fanpage.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-orange-600 hover:underline flex items-center gap-1"
              >
                Otwórz <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <CheckCircle className="w-5 h-5 text-green-500" />
        </div>
      </div>
      
      {/* Groups */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">
          Grupy ({places.groups?.length || 0})
        </h3>
        <div className="space-y-3">
          {places.groups?.map((group, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div>
                <div className="font-medium text-gray-900">{group.name}</div>
                {group.url && (
                  <a 
                    href={group.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-orange-600 hover:underline"
                  >
                    Otwórz
                  </a>
                )}
              </div>
              {group.isActive ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Clock className="w-5 h-5 text-gray-400" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Manual Token Input for FB publishing
function ManualTokenSection({ fbStatus, onTokenSaved }) {
  const [showManual, setShowManual] = useState(false);
  const [manualToken, setManualToken] = useState('');
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();
  
  const handleSaveToken = async () => {
    if (!manualToken.trim()) return;
    setSaving(true);
    try {
      const pageId = fbStatus?.selected_page?.id || '966901859847692';
      const pageName = fbStatus?.selected_page?.name || 'Apisai';
      
      const res = await fetch(`${API}/api/integrations/facebook/manual-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          page_token: manualToken.trim(),
          page_id: pageId,
          page_name: pageName
        })
      });
      
      if (res.ok) {
        toast.success('Token zapisany! Teraz możesz publikować posty.');
        setManualToken('');
        setShowManual(false);
        onTokenSaved?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Nieprawidłowy token');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
          <FileText className="w-4 h-4" />
          Problemy z publikacją?
        </div>
        <button
          onClick={() => setShowManual(!showManual)}
          className="text-xs text-amber-600 underline hover:text-amber-800"
          data-testid="toggle-manual-token"
        >
          {showManual ? 'Ukryj' : 'Wklej token ręcznie'}
        </button>
      </div>
      
      {showManual && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-amber-700 space-y-1">
            <p>1. Wejdź na <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer" className="underline font-medium">Graph API Explorer</a></p>
            <p>2. Wybierz aplikację "booster-pages" → zakładka Permissions</p>
            <p>3. Dodaj: pages_show_list, pages_manage_posts</p>
            <p>4. Kliknij "Generate Access Token"</p>
            <p>5. Wpisz <code className="bg-amber-100 px-1 rounded">me/accounts</code> → Submit</p>
            <p>6. Skopiuj <strong>access_token</strong> z wyniku i wklej poniżej</p>
          </div>
          <input
            type="text"
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            placeholder="Wklej Page Access Token..."
            className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="manual-token-input"
          />
          <Button
            onClick={handleSaveToken}
            disabled={!manualToken.trim() || saving}
            size="sm"
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="save-manual-token"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Zapisz token
          </Button>
        </div>
      )}
    </div>
  );
}

// Settings tab with Facebook Integration (P2.3)
function SettingsTab({ plan, onFbChanged }) {
  const { token } = useAuth();
  const [fbStatus, setFbStatus] = useState(null);
  const [fbPages, setFbPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [selectingPage, setSelectingPage] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Fetch FB status on mount
  useEffect(() => {
    fetchFacebookStatus();
  }, [token]);
  
  const fetchFacebookStatus = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/integrations/facebook/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFbStatus(data);
        
        // If connected but no page selected, fetch pages
        if (data.is_connected && !data.selected_page) {
          fetchFacebookPages();
        }
      }
    } catch (err) {
      console.error('Failed to fetch FB status:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFacebookPages = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/api/integrations/facebook/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFbPages(data.pages || []);
      }
    } catch (err) {
      console.error('Failed to fetch FB pages:', err);
    }
  };
  
  const handleConnectFacebook = async () => {
    setConnecting(true);
    
    try {
      const res = await fetch(`${API}/api/integrations/facebook/connect-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Redirect to Facebook OAuth
        window.location.href = data.oauth_url;
      } else {
        const err = await res.json();
        toast.error(err.detail?.message || 'Nie udało się połączyć z Facebookiem');
      }
    } catch (err) {
      console.error('FB connect error:', err);
      toast.error('Błąd połączenia z Facebookiem');
    } finally {
      setConnecting(false);
    }
  };
  
  const handleSelectPage = async (page) => {
    setSelectingPage(true);
    
    try {
      const res = await fetch(`${API}/api/integrations/facebook/pages/select`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ page_id: page.id, page_name: page.name })
      });
      
      if (res.ok) {
        toast.success(`Strona "${page.name}" wybrana!`);
        // Refresh status
        await fetchFacebookStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail?.message || 'Nie udało się wybrać strony');
      }
    } catch (err) {
      console.error('Page select error:', err);
      toast.error('Błąd wyboru strony');
    } finally {
      setSelectingPage(false);
    }
  };
  
  const handleDisconnect = async () => {
    if (!confirm('Czy na pewno chcesz odłączyć Facebooka?')) return;
    
    setDisconnecting(true);
    
    try {
      const res = await fetch(`${API}/api/integrations/facebook/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Facebook odłączony');
        setFbStatus({ is_connected: false });
        setFbPages([]);
      } else {
        toast.error('Nie udało się odłączyć');
      }
    } catch (err) {
      console.error('Disconnect error:', err);
      toast.error('Błąd odłączania');
    } finally {
      setDisconnecting(false);
    }
  };
  
  // Check URL params for FB callback result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('fb_connected') === 'true') {
      toast.success('Facebook połączony!');
      fetchFacebookStatus();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('fb_error') === 'true') {
      toast.error('Błąd połączenia z Facebookiem');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  
  return (
    <div className="space-y-6">
      {/* Facebook Integration Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Facebook (Fanpage)</h3>
              <p className="text-sm text-gray-500">Połącz konto, aby publikować posty</p>
            </div>
          </div>
          
          {fbStatus?.is_connected && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="fb-disconnect-button"
            >
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
              Odłącz
            </Button>
          )}
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-orange-600 animate-spin" />
          </div>
        ) : !fbStatus?.is_connected ? (
          /* Not connected - show connect button */
          <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <p className="text-gray-600 mb-4">Połącz swoje konto Facebook, aby publikować posty na swoim fanpage'u.</p>
            <Button
              onClick={handleConnectFacebook}
              disabled={connecting}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
              data-testid="fb-connect-button"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Link className="w-4 h-4" />
              )}
              Połącz z Facebookiem
            </Button>
          </div>
        ) : !fbStatus?.selected_page ? (
          /* Connected but no page selected - show page selection */
          <div>
            <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <span className="text-green-800 font-medium">Połączono jako: </span>
                <span className="text-green-700">{fbStatus.facebook_user_name}</span>
              </div>
            </div>
            
            <h4 className="font-medium text-gray-900 mb-3">Wybierz stronę do publikacji:</h4>
            
            {fbPages.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <p>Nie znaleziono stron.</p>
                <p className="text-sm mt-1">Upewnij się, że masz uprawnienia administratora do przynajmniej jednej strony.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchFacebookPages}
                  className="mt-3 gap-2"
                >
                  <Loader2 className="w-4 h-4" />
                  Odśwież listę
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {fbPages.map(page => (
                  <button
                    key={page.id}
                    onClick={() => handleSelectPage(page)}
                    disabled={selectingPage}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                    data-testid={`fb-page-${page.id}`}
                  >
                    <div>
                      <div className="font-medium text-gray-900">{page.name}</div>
                      {page.category && (
                        <div className="text-sm text-gray-500">{page.category}</div>
                      )}
                    </div>
                    {selectingPage ? (
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-gray-300" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Connected and page selected - show status */
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <span className="text-green-800 font-medium">Połączono jako: </span>
                <span className="text-green-700">{fbStatus.facebook_user_name}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <div className="flex-1">
                <div className="font-medium text-blue-900">Aktywna strona</div>
                <div className="text-blue-700">{fbStatus.selected_page?.name}</div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFbStatus(prev => ({ ...prev, selected_page: null }));
                  fetchFacebookPages();
                }}
                className="text-blue-600"
              >
                Zmień
              </Button>
            </div>
            
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-2 text-orange-800 font-medium mb-2">
                <Sparkles className="w-4 h-4" />
                Gotowe do publikacji!
              </div>
              <p className="text-sm text-orange-700">
                Przejdź do zakładki "Treści" i kliknij przycisk "Opublikuj na Facebooku" przy wybranym poście.
              </p>
            </div>
            
            {/* Manual Token Section */}
            <ManualTokenSection 
              fbStatus={fbStatus}
              onTokenSaved={() => {
                fetchFacebookStatus();
                onFbChanged?.();
              }}
            />
          </div>
        )}
      </div>
      
      {/* Plan Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Informacje o planie</h3>
        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-gray-500">Cel</dt>
            <dd className="text-gray-900 font-medium">{plan?.goal_type || '-'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Firma</dt>
            <dd className="text-gray-900 font-medium">{plan?.business_name || '-'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Długość</dt>
            <dd className="text-gray-900 font-medium">{plan?.duration || 7} dni</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// Main component
export default function BoosterV2Plan() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId } = useParams();
  const { token } = useAuth();
  
  const [plan, setPlan] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  
  // P2.3: Facebook integration state
  const [fbConnected, setFbConnected] = useState(false);
  
  // Preview drawer state
  const [previewPost, setPreviewPost] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pausing, setPausing] = useState(false);
  
  // Check FB connection status
  useEffect(() => {
    const checkFbStatus = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API}/api/integrations/facebook/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setFbConnected(data.is_connected && !!data.selected_page);
        }
      } catch (err) {
        console.error('FB status check failed:', err);
      }
    };
    checkFbStatus();
  }, [token]);
  
  const handlePreview = (post) => {
    setPreviewPost(post);
    setPreviewOpen(true);
  };
  
  const handlePreviewEdit = (post) => {
    // Close preview and scroll to the post, then trigger edit
    setPreviewOpen(false);
    setActiveTab('content');
    // The actual edit trigger would need to be handled by PostCard
    toast.info('Kliknij ikonę edycji przy poście');
  };
  
  useEffect(() => {
    fetchPlanData();
  }, [planId, token]);
  
  const fetchPlanData = async () => {
    if (!token || !planId) return;
    
    try {
      // Fetch plan
      const planRes = await fetch(`${API}/api/booster/v2/campaigns/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (planRes.ok) {
        setPlan(await planRes.json());
      }
      
      // Fetch content pack with posts
      const contentRes = await fetch(`${API}/api/booster/v2/campaigns/${planId}/content-pack`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (contentRes.ok) {
        const data = await contentRes.json();
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Failed to fetch plan:', err);
      toast.error('Nie udało się załadować planu');
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkPublished = async (post) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/posts/${post.id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'published' })
      });
      
      if (res.ok) {
        setPosts(posts.map(p => 
          p.id === post.id ? { ...p, status: 'published' } : p
        ));
        toast.success('Oznaczono jako opublikowany!');
      }
    } catch (err) {
      toast.error('Błąd aktualizacji');
    }
  };
  
  const handleStatusChange = async (post, newStatus) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/posts/${post.id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        setPosts(posts.map(p => 
          p.id === post.id ? { ...p, status: newStatus } : p
        ));
        
        const statusLabels = {
          draft: 'Cofnięto do szkicu',
          approved: 'Zatwierdzono do publikacji',
          published: 'Oznaczono jako opublikowany',
          skipped: 'Pominięto post'
        };
        toast.success(statusLabels[newStatus] || 'Status zmieniony');
      } else {
        toast.error('Nie udało się zmienić statusu');
      }
    } catch (err) {
      console.error('Status change error:', err);
      toast.error('Błąd połączenia');
    }
  };
  
  const handleSavePost = async (post, newText) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/posts/${post.id}/text`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: newText })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPosts(posts.map(p => 
          p.id === post.id ? { ...p, text: data.post?.text || newText } : p
        ));
        toast.success('Zapisano zmiany');
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || 'Nie udało się zapisać zmian');
        throw new Error('Save failed');
      }
    } catch (err) {
      console.error('Save post error:', err);
      toast.error('Błąd połączenia');
      throw err;
    }
  };
  
  const handleMediaUpdate = async (post, mediaUrls) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/post-items/${post.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ media: mediaUrls })
      });
      
      if (res.ok) {
        setPosts(posts.map(p => 
          p.id === post.id ? { ...p, media: mediaUrls } : p
        ));
      } else {
        throw new Error('Update failed');
      }
    } catch (err) {
      console.error('Media update error:', err);
      throw err;
    }
  };
  
  const handleSchedule = async (post, publishAt) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/posts/${post.id}/schedule`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ publish_at: publishAt })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPosts(posts.map(p => 
          p.id === post.id ? { ...p, ...data.post } : p
        ));
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || 'Nie udało się zaplanować');
        throw new Error('Schedule failed');
      }
    } catch (err) {
      console.error('Schedule error:', err);
      throw err;
    }
  };
  
  const handleBulkStatusChange = async (fromStatus, newStatus) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns/${planId}/posts/bulk-status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ from_status: fromStatus, new_status: newStatus })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
        
        const count = data.updated_count || 0;
        const statusLabels = {
          approved: `Zatwierdzono ${count} postów`,
          published: `Opublikowano ${count} postów`,
          draft: `Cofnięto ${count} postów do szkiców`
        };
        toast.success(statusLabels[newStatus] || `Zaktualizowano ${count} postów`);
      } else {
        toast.error('Nie udało się zaktualizować postów');
      }
    } catch (err) {
      console.error('Bulk status change error:', err);
      toast.error('Błąd połączenia');
    }
  };
  
  // P2.3: Publish post to Facebook
  const handlePublishFB = async (post) => {
    try {
      const res = await fetch(`${API}/api/booster/v2/posts/${post.id}/publish/facebook`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('FB publish - response parse error:', parseErr, 'status:', res.status);
        toast.error(`Błąd serwera (HTTP ${res.status}). Spróbuj ponownie.`);
        return;
      }
      
      if (data.ok) {
        toast.success('Post opublikowany na Facebooku!');
        setPosts(prev => prev.map(p => 
          p.id === post.id 
            ? { ...p, status: 'published', facebook_post_id: data.external_post_id }
            : p
        ));
      } else {
        // Show detailed error: message from API, or detail.message from HTTPException
        const errorMsg = data.message || data.detail?.message || data.detail || 'Nieznany błąd';
        const errorCode = data.error_code || data.detail?.error_code || '';
        console.error('FB publish failed:', { errorCode, errorMsg, requestId: data.request_id, fullResponse: data });
        toast.error(`Błąd FB: ${errorMsg}${errorCode ? ` (${errorCode})` : ''}`, { duration: 10000 });
      }
    } catch (err) {
      console.error('FB publish network error:', err);
      toast.error(`Błąd połączenia: ${err.message || 'Sprawdź internet i spróbuj ponownie'}`, { duration: 8000 });
    }
  };
  
  // Pause / Resume campaign
  const handleTogglePause = async () => {
    if (!plan) return;
    const isPaused = plan.status === 'paused';
    const endpoint = isPaused ? 'resume' : 'pause';
    
    setPausing(true);
    try {
      const res = await fetch(`${API}/api/booster/v2/campaigns/${planId}/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlan(prev => ({ ...prev, status: data.status }));
        toast.success(isPaused ? 'Kampania wznowiona!' : 'Kampania wstrzymana');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Nie udało się zmienić statusu');
      }
    } catch (err) {
      toast.error('Błąd połączenia');
    } finally {
      setPausing(false);
    }
  };

  // Build guidance checklist
  const buildChecklist = () => {
    const publishedCount = posts.filter(p => p.status === 'published').length;
    return [
      { label: 'Plan utworzony', status: 'done' },
      { label: 'Treści wygenerowane', status: posts.length > 0 ? 'done' : 'pending' },
      { 
        label: `${publishedCount}/${posts.length} opublikowanych`, 
        status: publishedCount === posts.length ? 'done' : 'current' 
      }
    ];
  };
  
  // Get next task
  const getNextTask = () => {
    const pendingPost = posts.find(p => p.status !== 'published' && p.status !== 'skipped');
    if (pendingPost) {
      return `Opublikuj post z dnia ${pendingPost.day_index}`;
    }
    return 'Wszystkie posty opublikowane!';
  };
  
  if (loading) {
    return (
      <PanelLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
        </div>
      </PanelLayout>
    );
  }
  
  return (
    <PanelLayout>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/panel/booster-v2-new')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {plan?.business_name || 'Plan promocji'}
                </h1>
                <p className="text-sm text-gray-500">
                  {plan?.goal_type || 'Brak celu'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {plan?.status === 'paused' ? (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                  <Pause className="w-3 h-3 inline mr-1" />
                  Wstrzymany
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  <Play className="w-3 h-3 inline mr-1" />
                  Aktywny
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleTogglePause}
                disabled={pausing}
                className={`gap-2 ${plan?.status === 'paused' ? 'border-green-200 text-green-700 hover:bg-green-50' : 'border-amber-200 text-amber-700 hover:bg-amber-50'}`}
                data-testid="pause-resume-button"
              >
                {pausing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : plan?.status === 'paused' ? (
                  <Play className="w-4 h-4" />
                ) : (
                  <Pause className="w-4 h-4" />
                )}
                {plan?.status === 'paused' ? 'Wznów' : 'Wstrzymaj'}
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-6 mt-4 border-b border-zinc-200">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-1 pb-3 text-sm font-medium transition-colors relative
                  ${activeTab === tab.id 
                    ? 'text-[#F47A3B]' 
                    : 'text-zinc-400 hover:text-zinc-700'
                  }
                `}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F47A3B] rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Left - Content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'summary' && (
              <SummaryTab plan={plan} posts={posts} />
            )}
            {activeTab === 'content' && (
              <ContentTab 
                posts={posts} 
                onStatusChange={handleStatusChange}
                onBulkStatusChange={handleBulkStatusChange}
                onCopy={() => {}}
                onEdit={() => {}}
                onSave={handleSavePost}
                onSchedule={handleSchedule}
                onPreview={handlePreview}
                onPublishFB={handlePublishFB}
                fbConnected={fbConnected}
                onMediaUpdate={handleMediaUpdate}
                groups={plan?.places?.groups || []}
              />
            )}
            {activeTab === 'places' && (
              <PlacesTab plan={plan} />
            )}
            {activeTab === 'settings' && (
              <SettingsTab plan={plan} onFbChanged={() => checkFBConnection()} />
            )}
          </div>
          
          {/* Right - Guidance */}
          <div className="w-[340px] flex-shrink-0 hidden lg:block">
            <GuidancePanel
              stepLabel="Plan aktywny"
              statusBadge="Aktywny"
              statusColor="bg-green-100 text-green-700"
              whyText="Publikuj treści zgodnie z planem, żeby budować zasięgi i pozyskiwać klientów."
              checklist={buildChecklist()}
              whatNow={getNextTask()}
              tipText="Najlepsze godziny publikacji: 8-9 rano, 12-13 w południe, 19-21 wieczorem."
            />
          </div>
        </div>
      </div>
      
      {/* Post Preview Drawer */}
      <PostPreviewDrawer
        post={previewPost}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        onCopy={() => {}}
        onEdit={handlePreviewEdit}
      />
    </div>
    </PanelLayout>
  );
}
